import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  LoadedPlatformContext,
  loadPlatformContext,
  logActivity as logActivityService,
  LogActivityParams,
  PlatformBranding,
  PlatformTenant,
  TenantAppAccess,
} from '../services/platformContext';
import { useAuth } from './AuthContext';

export interface PlatformContextValue {
  tenantId: string | null;
  appSlug: string;
  tenant: PlatformTenant | null;
  branding: PlatformBranding;
  enabled: boolean;
  availableApps: TenantAppAccess[];
  isLoading: boolean;
  error: string | null;
  usedFallback: boolean;
  hostname: string;
  refreshPlatformContext: () => Promise<void>;
  logActivity: (params: Omit<LogActivityParams, 'userId' | 'tenantId' | 'appSlug'> & { appSlug?: string }) => Promise<void>;
}

const PlatformContext = createContext<PlatformContextValue | undefined>(undefined);

const INITIAL: LoadedPlatformContext = {
  tenantId: null,
  appSlug: 'climateplans',
  enabled: false,
  tenant: null,
  branding: {},
  availableApps: [],
  usedFallback: true,
  hostname: typeof window !== 'undefined' ? window.location.hostname : 'localhost',
};

interface PlatformProviderProps {
  children: ReactNode;
}

export const PlatformProvider: React.FC<PlatformProviderProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [contextState, setContextState] = useState<LoadedPlatformContext>(INITIAL);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const openedRef = useRef(false);
  const prevUserIdRef = useRef<string | null | undefined>(undefined);
  const stateRef = useRef(contextState);

  useEffect(() => {
    stateRef.current = contextState;
  }, [contextState]);

  const logActivity = useCallback(
    async (params: Omit<LogActivityParams, 'userId' | 'tenantId' | 'appSlug'> & { appSlug?: string }) => {
      const current = stateRef.current;
      await logActivityService({
        ...params,
        appSlug: params.appSlug ?? current.appSlug,
        tenantId: current.tenantId,
        userId: user?.id ?? null,
        metadata: {
          tenant_slug: current.tenant?.slug,
          ...(params.metadata ?? {}),
        },
      });
    },
    [user?.id]
  );

  const refreshPlatformContext = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const loaded = await loadPlatformContext();
      setContextState(loaded);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load platform context';
      setError(message);
      if (process.env.NODE_ENV === 'development') {
        console.warn('[platform] refreshPlatformContext failed', err);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const loaded = await loadPlatformContext();
        if (cancelled) return;
        setContextState(loaded);

        if (!openedRef.current) {
          openedRef.current = true;
          await logActivityService({
            action: 'app.opened',
            appSlug: loaded.appSlug,
            tenantId: loaded.tenantId,
            userId: user?.id ?? null,
            metadata: {
              hostname: loaded.hostname,
              path: window.location.pathname,
              tenant_slug: loaded.tenant?.slug,
              used_fallback: loaded.usedFallback,
            },
          });
        }
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load platform context';
        setError(message);
        if (process.env.NODE_ENV === 'development') {
          console.warn('[platform] initial load failed', err);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (authLoading) return;

    const currentUserId = user?.id ?? null;
    const previousUserId = prevUserIdRef.current;

    if (previousUserId === undefined) {
      prevUserIdRef.current = currentUserId;
      return;
    }

    if (previousUserId === null && currentUserId) {
      void logActivity({ action: 'auth.signed_in' });
    } else if (previousUserId && currentUserId === null) {
      void logActivity({ action: 'auth.signed_out' });
    }

    prevUserIdRef.current = currentUserId;
  }, [authLoading, user?.id, logActivity]);

  const value = useMemo<PlatformContextValue>(
    () => ({
      tenantId: contextState.tenantId,
      appSlug: contextState.appSlug,
      tenant: contextState.tenant,
      branding: contextState.branding,
      enabled: contextState.enabled,
      availableApps: contextState.availableApps,
      isLoading,
      error,
      usedFallback: contextState.usedFallback,
      hostname: contextState.hostname,
      refreshPlatformContext,
      logActivity,
    }),
    [contextState, isLoading, error, refreshPlatformContext, logActivity]
  );

  return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>;
};

export function usePlatform(): PlatformContextValue {
  const context = useContext(PlatformContext);
  if (context === undefined) {
    throw new Error('usePlatform must be used within a PlatformProvider');
  }
  return context;
}

export default PlatformContext;
